import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Linkedin, Twitter, Mail, Phone, MapPin } from "lucide-react";
import { LINKS, BRAND, TEAM } from "@/lib/content";
import { useT } from "@/lib/i18n";

export default function Footer() {
    const { t } = useT();
    const year = new Date().getFullYear();
    const contacts = TEAM;
    return (
        <footer
            className="border-t border-white/10 bg-[color:var(--bg-base)]"
            data-testid="site-footer"
        >
            <div className="container-x py-16 md:py-20">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 pb-12 border-b border-white/8">
                    <div className="max-w-2xl">
                        <p className="eyebrow mb-4">{t.footer.eyebrow}</p>
                        <h3 className="font-display text-3xl md:text-4xl leading-tight tracking-tight text-white">
                            {t.footer.heading}
                        </h3>
                        <p className="mt-4 text-white/60 text-[0.98rem] leading-relaxed">
                            {t.footer.subheading}
                        </p>
                    </div>
                    <a
                        href={LINKS.consultation}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary"
                        data-testid="footer-cta-consultation"
                    >
                        {t.footer.cta}
                    </a>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 pt-12">
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-3 mb-5">
                            <img
                                src={BRAND.logo}
                                alt="SalesShark"
                                className="h-11 w-11 object-contain"
                                loading="lazy"
                            />
                            <span className="font-display text-2xl font-semibold tracking-tight text-white">
                                SalesShark
                            </span>
                        </div>
                        <p className="text-white/50 text-sm mb-6 max-w-md leading-relaxed">
                            {t.footer.tagline}
                        </p>
                        <address className="not-italic text-white/70 text-sm leading-relaxed space-y-1">
                            <div className="text-white font-medium">
                                {t.contact.company.name}
                            </div>
                            <div className="flex items-start gap-2">
                                <MapPin
                                    size={14}
                                    strokeWidth={1.5}
                                    className="mt-1 shrink-0 text-white/40"
                                />
                                <span>
                                    {t.contact.company.addressLine1}
                                    <br />
                                    {t.contact.company.addressLine2}
                                </span>
                            </div>
                            <div className="text-white/50 pt-1">
                                {t.contact.company.id}
                            </div>
                        </address>
                    </div>

                    <div className="md:col-span-4">
                        <h4 className="text-white text-sm font-semibold tracking-wider uppercase mb-5">
                            {t.footer.contactHeading}
                        </h4>
                        <ul className="space-y-4 text-sm">
                            <li>
                                <div className="text-white/60 text-xs mb-1">
                                    {t.footer.roles.antonia}
                                </div>
                                <div className="space-y-1.5">
                                    <a
                                        href={`mailto:${contacts[0].email}`}
                                        className="flex items-center gap-2 text-white/85 hover:text-[color:var(--accent)] transition-colors"
                                        data-testid="footer-email-antonia"
                                    >
                                        <Mail size={14} strokeWidth={1.5} />
                                        {contacts[0].email}
                                    </a>
                                    <a
                                        href={contacts[0].phoneHref}
                                        className="flex items-center gap-2 text-white/85 hover:text-[color:var(--accent)] transition-colors"
                                        data-testid="footer-phone-antonia"
                                    >
                                        <Phone size={14} strokeWidth={1.5} />
                                        {contacts[0].phone}
                                    </a>
                                </div>
                            </li>
                            <li>
                                <div className="text-white/60 text-xs mb-1">
                                    {t.footer.roles.antal}
                                </div>
                                <div className="space-y-1.5">
                                    <a
                                        href={`mailto:${contacts[1].email}`}
                                        className="flex items-center gap-2 text-white/85 hover:text-[color:var(--accent)] transition-colors"
                                        data-testid="footer-email-antal"
                                    >
                                        <Mail size={14} strokeWidth={1.5} />
                                        {contacts[1].email}
                                    </a>
                                    <a
                                        href={contacts[1].phoneHref}
                                        className="flex items-center gap-2 text-white/85 hover:text-[color:var(--accent)] transition-colors"
                                        data-testid="footer-phone-antal"
                                    >
                                        <Phone size={14} strokeWidth={1.5} />
                                        {contacts[1].phone}
                                    </a>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <div className="md:col-span-3">
                        <h4 className="text-white text-sm font-semibold tracking-wider uppercase mb-5">
                            {t.footer.menuHeading}
                        </h4>
                        <ul className="space-y-3 text-sm">
                            <li>
                                <Link
                                    to="/"
                                    className="text-white/70 hover:text-white transition-colors"
                                    data-testid="footer-nav-home"
                                >
                                    {t.nav.home}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/services"
                                    className="text-white/70 hover:text-white transition-colors"
                                    data-testid="footer-nav-services"
                                >
                                    {t.nav.services}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/#rolunk"
                                    className="text-white/70 hover:text-white transition-colors"
                                    data-testid="footer-nav-about"
                                >
                                    {t.nav.about}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/#referenciak"
                                    className="text-white/70 hover:text-white transition-colors"
                                    data-testid="footer-nav-references"
                                >
                                    {t.nav.references}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/#kapcsolat"
                                    className="text-white/70 hover:text-white transition-colors"
                                    data-testid="footer-nav-contact"
                                >
                                    {t.nav.contact}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/faq"
                                    className="text-white/70 hover:text-white transition-colors"
                                    data-testid="footer-nav-faq"
                                >
                                    {t.nav.faq}
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-12 pt-6 border-t border-white/8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div className="text-white/40 text-xs">
                        {t.footer.copyright(year)}
                    </div>
                    <div className="flex items-center gap-3">
                        <a
                            href={LINKS.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Facebook"
                            className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-white/10 text-white/70 hover:text-white hover:border-white/25 transition-colors"
                            data-testid="footer-social-facebook"
                        >
                            <Facebook size={16} strokeWidth={1.5} />
                        </a>
                        <a
                            href={LINKS.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="LinkedIn"
                            className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-white/10 text-white/70 hover:text-white hover:border-white/25 transition-colors"
                            data-testid="footer-social-linkedin"
                        >
                            <Linkedin size={16} strokeWidth={1.5} />
                        </a>
                        <a
                            href={LINKS.twitter}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Twitter"
                            className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-white/10 text-white/70 hover:text-white hover:border-white/25 transition-colors"
                            data-testid="footer-social-twitter"
                        >
                            <Twitter size={16} strokeWidth={1.5} />
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
}
